import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GrowlerService } from '../../../../shared/growler/growler.service';

export const LOGO_MAX_WIDTH = 225;
export const LOGO_MAX_HEIGHT = 94;

@Component({
    selector: 'app-organization-logo-dialog',
    templateUrl: './organization-logo-dialog.component.html',
    standalone: true,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OrganizationLogoDialogComponent implements OnChanges {
    @Input() open = false;
    @Input() saving = false;
    /** A file already picked outside the dialog (the inline drop zone), loaded as soon as it opens. */
    @Input() initialFile: File | null = null;
    @Input() heading = 'Organization logo';
    @Input() maxWidth = LOGO_MAX_WIDTH;
    @Input() maxHeight = LOGO_MAX_HEIGHT;
    @Output() save = new EventEmitter<string>();
    @Output() closed = new EventEmitter<void>();

    readonly accept = 'image/png,image/jpeg,image/gif,image/webp';

    get recommendedSize(): string {
        return `${this.maxWidth} x ${this.maxHeight}px`;
    }

    get hint(): string {
        return `Recommended image size: ${this.recommendedSize}. Supported file types: PNG, JPG, GIF, WEBP.`;
    }

    logoDataUrl: string | null = null;
    fileName = '';
    underSized = false;

    constructor(private _growler: GrowlerService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialFile'] && this.initialFile) {
            this.loadFile(this.initialFile);
        }
    }

    onFiles(event: CustomEvent<File[]>): void {
        this.loadFile(event.detail?.[0]);
    }

    private loadFile(file: File | undefined): void {
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => this.fitToLogoBox(img, file.name);
            img.onerror = () => this.invalidImage();
            img.src = reader.result as string;
        };
        reader.onerror = () => this.invalidImage();
        reader.readAsDataURL(file);
    }

    clear(): void {
        this.logoDataUrl = null;
        this.fileName = '';
        this.underSized = false;
    }

    submit(): void {
        if (this.logoDataUrl && !this.saving) {
            this.save.emit(this.logoDataUrl);
        }
    }

    close(): void {
        if (!this.saving) {
            this.closed.emit();
        }
    }

    private fitToLogoBox(img: HTMLImageElement, name: string): void {
        const scale = Math.min(1, this.maxWidth / img.width, this.maxHeight / img.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(img.width * scale));
        canvas.height = Math.max(1, Math.floor(img.height * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        this.logoDataUrl = canvas.toDataURL('image/png');
        this.fileName = name;
        this.underSized = img.width < this.maxWidth && img.height < this.maxHeight;
    }

    private invalidImage(): void {
        this._growler.error(
            'Invalid Image',
            'The selected file is not a valid image. The file extension may not match the actual file content.',
        );
        this.clear();
    }
}
